/**
 * Normalize a province/state code from any source into the canonical
 * `PROVINCE_NAMES` key format, which uses an UNDERSCORE separator (e.g. "DE_BY").
 *
 *  - Tolerant Post sends a bare code ("BY")              -> "DE_BY"
 *  - Retail CRM already sends the prefixed code ("DE_BY") -> "DE_BY" (unchanged)
 *  - Legacy hyphenated values ("DE-BY")                  -> "DE_BY"
 *
 * Returns the input unchanged when it can't be resolved (e.g. a bare code with
 * no country to supply the prefix), and passes through empty/undefined input.
 */
export function normalizeProvinceCode(rawCode: string | undefined, country: string | undefined): string | undefined {
  if (!rawCode) return rawCode;

  const countryCode = country?.toUpperCase();

  // Already prefixed (e.g. "DE_BY" or legacy "DE-BY")? Normalize the separator.
  const prefixed = /^([A-Za-z]{2})[_-](.+)$/.exec(rawCode);
  if (prefixed) {
    return `${prefixed[1].toUpperCase()}_${prefixed[2].toUpperCase()}`;
  }

  // Bare code (e.g. "BY") -> prefix with the country when available.
  return countryCode ? `${countryCode}_${rawCode.toUpperCase()}` : rawCode;
}

/**
 * Convert a province/state code into GCDM's `address.regionCode`, which carries only the
 * region part without the country prefix ("DE_BY" -> "BY").
 *
 * The GCDM metadata api-key ("DE-BY") and CDF's canonical key ("DE_BY") share the same data
 * source, so stripping the prefix is sufficient - no metadata lookup is needed (CUSTAPP-611).
 * The inverse of `normalizeProvinceCode`, which is used when reading GCDM addresses.
 */
export function toGcdmRegionCode(rawCode: string | undefined, country: string | undefined): string | undefined {
  const normalized = normalizeProvinceCode(rawCode, country);
  if (!normalized) return normalized;

  const [, region] = /^[A-Z]{2}_(.+)$/.exec(normalized) ?? [];
  return region ?? normalized;
}
